import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Alert,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { nl } from "date-fns/locale";
import emailjs from "@emailjs/browser";
import { SelectChangeEvent } from "@mui/material/Select";

// Styled Components
const StyledFormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
}));

const StyledFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const StyledFormSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
}));

const StyledFormDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 0),
}));

const StyledSuccessAlert = styled(Alert)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
}));

const StyledErrorAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledSubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// Define the form data interface
interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  petName: string;
  dogBreed: string;
  serviceType: string;
  startDate: string;
  endDate: string;
  message: string;
}

export const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    petName: "",
    dogBreed: "",
    serviceType: "",
    startDate: "",
    endDate: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      
      // Clear error for this field when user starts typing again
      if (formErrors[name as keyof ContactFormData]) {
        setFormErrors(prev => ({
          ...prev,
          [name]: undefined
        }));
      }
    }
  };

  // Validate form data
  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof ContactFormData, string>> = {};
    let isValid = true;

    // Email validation with regex
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = "Voer een geldig e-mailadres in";
      isValid = false;
    }

    // Phone validation - Dutch phone format (allowing international format too)
    const phoneRegex = /^(\+[0-9]{1,3})?[-\s]?([0-9]{6,14})$/;
    if (!phoneRegex.test(formData.phone)) {
      errors.phone = "Voer een geldig telefoonnummer in";
      isValid = false;
    }

    // Basic validation for other required fields
    if (formData.name.trim().length < 2) {
      errors.name = "Naam moet minimaal 2 tekens bevatten";
      isValid = false;
    }

    if (formData.petName.trim().length < 2) {
      errors.petName = "Naam huisdier moet minimaal 2 tekens bevatten";
      isValid = false;
    }

    if (formData.dogBreed.trim().length < 2) {
      errors.dogBreed = "Hondenras moet minimaal 2 tekens bevatten";
      isValid = false;
    }

    if (!formData.serviceType) {
      errors.serviceType = "Selecteer een type dienst";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      // Prepare email template parameters
      const templateParams = {
        to_email: "pippaya.nl@gmail.com",
        to_name: "Pippaya",
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        pet_name: formData.petName,
        dog_breed: formData.dogBreed,
        service_type: formData.serviceType,
        start_date: formData.startDate,
        end_date: formData.endDate,
        message: formData.message,
      };

      // Send email using EmailJS with environment variables
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID!,
        templateParams
      );

      // Reset form and show success message
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        petName: "",
        dogBreed: "",
        serviceType: "",
        startDate: "",
        endDate: "",
        message: "",
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error("Error:", error);
      setError("Er is een fout opgetreden bij het versturen van uw aanvraag. Probeer het later opnieuw.");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
      <StyledFormPaper elevation={3}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledFormTitle variant="h4">
                Reserveren bij Pippaya 🐾
              </StyledFormTitle>
              <StyledFormSubtitle variant="body1">
                Wil je weten of er plek is voor jouw hond? Stuur dan een reserveringsaanvraag met de gewenste opvangperiode. Omdat Pippaya bewust kleinschalig is, kijk ik bij iedere aanvraag niet alleen naar de beschikbare plek, maar ook naar de honden die in die periode al bij ons verblijven. Zo kan ik ervoor zorgen dat de groep prettig bij elkaar past.
              </StyledFormSubtitle>
              <Typography variant="body2" color="text.secondary" paragraph>
                Na je aanvraag ontvang je van mij bericht. Wanneer de opvang mogelijk is, ontvang je een offerte via Moneybird. <strong>Pas nadat deze offerte online is goedgekeurd, is de reservering definitief.</strong> Voor een eerste verblijf maken we in principe eerst kennis.
              </Typography>
              <StyledFormDivider />
            </Grid>

            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Naam"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                inputProps={{
                  pattern: "[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Telefoonnummer"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                inputProps={{
                  inputMode: "tel",
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Naam Huisdier"
                name="petName"
                value={formData.petName}
                onChange={handleInputChange}
                error={!!formErrors.petName}
                helperText={formErrors.petName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Hondenras"
                name="dogBreed"
                value={formData.dogBreed}
                onChange={handleInputChange}
                placeholder="Bijv. Labrador, Golden Retriever, etc."
                error={!!formErrors.dogBreed}
                helperText={formErrors.dogBreed}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!formErrors.serviceType}>
                <InputLabel id="service-type-label">Type Dienst</InputLabel>
                <Select
                  labelId="service-type-label"
                  name="serviceType"
                  value={formData.serviceType}
                  label="Type Dienst"
                  onChange={handleInputChange}
                >
                  <MenuItem value="feestdagopvang">Feestdagopvang</MenuItem>
                  <MenuItem value="weekendopvang">Weekendopvang</MenuItem>
                  <MenuItem value="vakantieopvang">Vakantieopvang</MenuItem>
                  <MenuItem value="dagopvang">Dagopvang</MenuItem>
                  <MenuItem value="anders">Anders</MenuItem>
                </Select>
                <FormHelperText>{formErrors.serviceType || "Selecteer het type dienst waar u interesse in heeft"}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gewenste eerste opvangdag"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gewenste laatste opvangdag"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bericht"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                multiline
                rows={4}
                placeholder="Vertel hier eventueel iets extra’s over je hond of de opvangwensen."
              />
            </Grid>

            <Grid item xs={12}>
              {submitted && (
                <StyledSuccessAlert severity="success">
                  Bedankt voor je reserveringsaanvraag! Ik neem zo snel mogelijk contact met je op. Let op: de reservering is pas definitief nadat de Moneybird-offerte online is goedgekeurd.
                </StyledSuccessAlert>
              )}
              
              {error && (
                <StyledErrorAlert severity="error">
                  {error}
                </StyledErrorAlert>
              )}
              
              <StyledSubmitButton
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                fullWidth
              >
                Verstuur reserveringsaanvraag
              </StyledSubmitButton>
            </Grid>
          </Grid>
        </form>
      </StyledFormPaper>
    </LocalizationProvider>
  );
}; 